import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import TransactionSummary from './TransactionSummary';
import TransactionActions from './TransactionActions';
import TransactionFormDialog from './TransactionFormDialog';
import TransactionList, { detectPaymentMethod } from './TransactionList';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { formatCurrency } from '../../lib/formatters';

const TransactionsTab = ({
  transactions,
  deal,
  selectedTransactions,
  setSelectedTransactions,
  transactionDialogOpen,
  setTransactionDialogOpen,
  transactionForm,
  setTransactionForm,
  editingTransaction,
  setEditingTransaction,
  onAddTransaction,
  onDeleteTransaction,
  onBulkDelete,
  onOpenMoveDialog,
  onBulkEdit,
  onDownloadCSV,
  onMarkBounced,
  isAdmin,
  onRefundTransaction,
  onBounceTransaction,
  onRelinkReturn,
  getAuthHeaders,
  onUploadComplete,
  onDeferred,
  onReverseDefer,
}) => {
  const [typeFilter, setTypeFilter] = useState(null);
  // 08/20 (Josh): payment-method filter — "only see zelles"
  const [methodFilter, setMethodFilter] = useState(null);
  const methodCounts = (transactions || [])
    .filter(t => t.type === 'PAYMENT')
    .reduce((acc, t) => { const m = detectPaymentMethod(t); acc[m] = (acc[m] || 0) + 1; return acc; }, {});
  const methodOptions = Object.keys(methodCounts).sort();

  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    const canonicalTypes = [
      'PAYMENT', 'REFUND', 'LAWYER_COLLECTION', 'DISCOUNT', 'FEE_DISCOUNT',
      'DEFAULT_FEE', 'DEFERRED_FEE', 'BOUNCE_FEE', 'ADJUSTMENT_INCREASE', 'ACH_RETURN',
      'WRITE_OFF', 'LAWYER_FEE', 'REFINANCE_PAYOFF', 'EARLY_PAYOFF'
    ];
    let editType = transaction.type;
    if (!canonicalTypes.includes(editType)) {
      const storedType = transaction.type;
      if (['ACH', 'Wire', 'Processor', 'Check', 'Cash', 'Zelle'].includes(storedType)) editType = 'PAYMENT';
      else if (storedType === 'Refinance Payoff' || storedType === 'REFINANCE_PAYOFF') editType = 'REFINANCE_PAYOFF';
      else if (storedType === 'Early Payoff' || storedType === 'EARLY_PAYOFF') editType = 'EARLY_PAYOFF';
      else if (storedType === 'Manual Adjustment') editType = 'ADJUSTMENT_INCREASE';
      else if (storedType === 'Discount') editType = 'DISCOUNT';
      else if (storedType.startsWith('Fee') || storedType.toLowerCase().includes('bounce') || /R\d{2}/.test(storedType)) editType = 'BOUNCE_FEE';
    }
    setTransactionForm({
      date: new Date(transaction.date).toISOString().split('T')[0],
      amount: Math.abs(transaction.amount).toString(),
      type: editType,
      description: '',
      reference_number: transaction.reference_number || '',
      notes: transaction.notes || ''
    });
    setTransactionDialogOpen(true);
  };

  const toggleTransactionSelection = (transactionId) => {
    setSelectedTransactions(prev =>
      prev.includes(transactionId)
        ? prev.filter(tid => tid !== transactionId)
        : [...prev, transactionId]
    );
  };

  // Bulk-set a range of ids to a single target state. Used by shift+click range
  // selection in the transactions table (Gmail/Finder pattern). Pure setter
  // semantics — `targetChecked=true` adds every id in `ids` to the selection,
  // `targetChecked=false` removes them. Other selections are preserved.
  const setRangeSelection = (ids, targetChecked) => {
    if (!ids || !ids.length) return;
    setSelectedTransactions(prev => {
      const set = new Set(prev);
      if (targetChecked) {
        ids.forEach(id => set.add(id));
      } else {
        ids.forEach(id => set.delete(id));
      }
      return Array.from(set);
    });
  };

  const toggleSelectAll = (visibleIds, allSelected) => {
    if (!visibleIds) {
      // Defensive: legacy callers pass nothing — fall back to all
      setSelectedTransactions(prev => prev.length === transactions.length ? [] : transactions.map(t => t.id));
      return;
    }
    setSelectedTransactions(prev => {
      const set = new Set(prev);
      if (allSelected) {
        // Deselect only the visible rows; keep any other selections intact
        visibleIds.forEach(id => set.delete(id));
      } else {
        visibleIds.forEach(id => set.add(id));
      }
      return Array.from(set);
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <CardTitle>Transaction History</CardTitle>
            <TransactionSummary
              transactions={transactions}
              deal={deal}
              typeFilter={typeFilter}
              setTypeFilter={setTypeFilter}
            />
            {methodOptions.length > 0 && (
              <div className="mt-2 flex items-center gap-2">
                <Select value={methodFilter || 'all'}
                  onValueChange={(v) => setMethodFilter(v === 'all' ? null : v)}>
                  <SelectTrigger className="h-8 w-44 text-xs" data-testid="method-filter">
                    <SelectValue placeholder="All payment methods" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All payment methods</SelectItem>
                    {methodOptions.map(m => (
                      <SelectItem key={m} value={m}>{m} ({methodCounts[m]})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {methodFilter && (
                  <button type="button" className="text-xs text-blue-600 hover:underline"
                    onClick={() => setMethodFilter(null)} data-testid="clear-method-filter">
                    Clear
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <TransactionActions
              deal={deal}
              selectedTransactions={selectedTransactions}
              isAdmin={isAdmin}
              getAuthHeaders={getAuthHeaders}
              onBulkDelete={onBulkDelete}
              onOpenMoveDialog={onOpenMoveDialog}
              onBulkEdit={onBulkEdit}
              onDownloadCSV={onDownloadCSV}
              onUploadComplete={onUploadComplete}
            />
            {isAdmin && (
              <TransactionFormDialog
                deal={deal}
                transactionDialogOpen={transactionDialogOpen}
                setTransactionDialogOpen={setTransactionDialogOpen}
                transactionForm={transactionForm}
                setTransactionForm={setTransactionForm}
                editingTransaction={editingTransaction}
                setEditingTransaction={setEditingTransaction}
                onAddTransaction={onAddTransaction}
                getAuthHeaders={getAuthHeaders}
                onDeferred={onDeferred}
              />
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {(() => {
          // Overage banner — shown only when balance is fully paid AND collected exceeds payback.
          // Uses the deal as source of truth (server-recomputed values), never an upload snapshot.
          const balance = Number(deal?.balance ?? 0);
          const balanceWithFees = Number(deal?.balance_with_fees ?? deal?.balance ?? 0);
          const netCollected = Number(deal?.payments_made ?? 0);
          const payback = Number(deal?.payback ?? 0);
          const overage = +(netCollected - payback).toFixed(2);
          // Genuine overage = principal balance fully paid (or negative) AND we've collected more than payback.
          // We check balance_with_fees too so deals with outstanding fees don't trigger the banner.
          const showBanner = payback > 0 && balance <= 0.01 && balanceWithFees <= 0.01 && overage > 0.01;
          if (!showBanner) return null;
          return (
            <div className="mx-4 mt-4 mb-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-3" data-testid="overage-banner">
              <div className="text-blue-600 mt-0.5">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
              </div>
              <div className="text-sm text-blue-900">
                <span className="font-semibold">Net collected ({formatCurrency(netCollected)}) exceeds payback ({formatCurrency(payback)}) by {formatCurrency(overage)}.</span>{' '}
                This overage is available to transfer to another deal or refund to the merchant.
              </div>
            </div>
          );
        })()}
        <TransactionList
          transactions={transactions}
          deal={deal}
          selectedTransactions={selectedTransactions}
          typeFilter={typeFilter}
          methodFilter={methodFilter}
          isAdmin={isAdmin}
          onToggleSelection={toggleTransactionSelection}
          onRangeSelect={setRangeSelection}
          onToggleSelectAll={toggleSelectAll}
          onEditTransaction={handleEditTransaction}
          onDeleteTransaction={onDeleteTransaction}
          onMarkBounced={onMarkBounced}
          onRefundTransaction={onRefundTransaction}
          onBounceTransaction={onBounceTransaction}
          onRelinkReturn={onRelinkReturn}
          onReverseDefer={onReverseDefer}
        />
      </CardContent>
    </Card>
  );
};

export default TransactionsTab;
